import '@fontsource-variable/inter';
import '@fontsource-variable/noto-sans-jp';
import 'yakuhanjp';
import {
  colorsTuple,
  createTheme,
  CSSVariablesResolver,
  defaultVariantColorsResolver,
  parseThemeColor,
  VariantColorsResolver,
} from '@mantine/core';
import tokens from '../../design/tokens.json';

const { color } = tokens.shared;
const { dark } = color;

// Mantine は light scheme の枠線（AppShell / Table / Divider / Popover）を gray-3 から
// 引くので、その位置に共有の border token を置く
const gray = colorsTuple([
  ...color.scale.zinc.slice(0, 3),
  color.border,
  ...color.scale.zinc.slice(4),
]);

// Mantine の dark scheme は 0 が最明・9 が最暗で、4 を枠線・6 を入力の面・7 を
// --mantine-color-body（Paper / Menu / Modal / Drawer）に使う
const darkScale = colorsTuple([
  dark.text.primary,
  dark.text.primary,
  dark.text.secondary,
  dark.text.muted,
  dark.border,
  dark.border,
  dark.surfaceRaised,
  dark.surfaceRaised,
  dark.surface,
  dark.background.base,
]);

// Mantine は filled の上に載る文字を白で固定する。dark の主色は面より明るく白文字が読めないので、
// 主色の filled だけ scheme ごとに配る contrast 変数に差し替える
const variantColorResolver: VariantColorsResolver = (input) => {
  const resolved = defaultVariantColorsResolver(input);
  const parsed = parseThemeColor({
    color: input.color ?? input.theme.primaryColor,
    theme: input.theme,
  });
  // shade 指定（blue.7 など）の filled は固定の shade を敷くので、上書きの対象外
  if (
    input.variant === 'filled' &&
    parsed.shade === undefined &&
    parsed.color === input.theme.primaryColor
  ) {
    return { ...resolved, color: 'var(--mantine-primary-color-contrast)' };
  }
  return resolved;
};

export const theme = createTheme({
  primaryColor: tokens.apps.front.color.primary,
  primaryShade: { light: 5, dark: 5 },
  black: color.text.primary,
  fontFamily: tokens.shared.font.sans,
  headings: { fontFamily: tokens.shared.font.sans },
  defaultRadius: 'md',
  variantColorResolver,
  radius: { xs: '0.25rem', sm: '0.5rem', md: '0.75rem', lg: '1rem', xl: '1.5rem' },
  colors: {
    blue: colorsTuple(color.scale.blue),
    gray,
    dark: darkScale,
    // 単色の token を全 shade に敷く。light / outline variant で濃淡が要るなら scale を token に足す
    red: colorsTuple(color.error),
    green: colorsTuple(color.success),
    yellow: colorsTuple(tokens.apps.front.color.warning),
  },
});

// 地色（body）と面（Paper / Menu / Modal / Drawer）は Mantine ではどちらも
// --mantine-color-body なので、地色とヘッダー / ナビの色は別の変数で配り、global.css で当てる
export const cssVariablesResolver: CSSVariablesResolver = () => ({
  variables: {},
  light: {
    '--plamo-page-bg': color.background.base,
    '--plamo-shell-bg': color.surface,
    // ヘッダーの丸ボタンの面。chat_ui の --muted と同じ
    '--plamo-muted': color.scale.zinc[1],
    // 状態を示す図形に使うので WCAG 1.4.11 の 3:1 が要る。共有の success は白地に 2.11:1
    '--mantine-color-green-text': tokens.apps.front.color.successText,
    // 共有の error は白地に 3.76:1。light は 5 を文字（-text は -filled）、6 を
    // --mantine-color-error と outline に引く
    '--mantine-color-red-5': tokens.apps.front.color.errorText,
    '--mantine-color-red-6': tokens.apps.front.color.errorText,
  },
  dark: {
    '--plamo-page-bg': dark.surface,
    '--plamo-shell-bg': dark.background.base,
    '--plamo-muted': dark.surfaceRaised,
    // placeholder の既定 dark-3 は入力の面に対して 3.1:1 しかない
    '--mantine-color-placeholder': dark.text.secondary,
    // Mantine が dark の文字・枠に使う赤。単色の error は dark の面で 3.9:1 しかない。
    // primaryShade.dark=5 のとき light variant は 0、outline は 1、text は 4、
    // --mantine-color-error は 8 を引く。6 は filled の hover なので上書きしない
    // （明るくすると白文字が 2.8:1 まで落ちる）
    '--mantine-color-red-0': tokens.apps.front.color.dark.error,
    '--mantine-color-red-1': tokens.apps.front.color.dark.error,
    '--mantine-color-red-4': tokens.apps.front.color.dark.error,
    '--mantine-color-red-8': tokens.apps.front.color.dark.error,
    // Anchor と c="blue" が引く shade
    '--mantine-color-blue-4': dark.primaryBlue,
    // 主色の塗りは chat_ui と同じ明るい青にする。filled の上の文字は白では 2.0:1 なので地色
    '--mantine-color-blue-filled': dark.primaryBlue,
    '--mantine-color-blue-filled-hover': color.scale.blue[3],
    '--mantine-primary-color-contrast': dark.surface,
  },
});
